'use client'

import {
    Button,
    ButtonVariants,
    DictionaryText,
    Modal,
    ModalSizes,
    Text,
    STEPPED_MODAL_MESSAGE_KEYS,
    SteppedModalContext,
    ModalProps,
} from '../../..'
import { TextTypes } from '../../../types'
import React, { ReactElement, use, useState } from 'react'

interface Props extends Pick<ModalProps, 'size'> { }

export const SteppedModal = ({ size = ModalSizes.XXXL }: Props): ReactElement => {
    const modalContext = use(SteppedModalContext)
    if (!modalContext) {
        console.error('SteppedModalContext not found')
        return <></>
    }
    const { isModalVisible, setModalVisibility, markModalAsSeen, steps } = modalContext
    const [currentStep, setCurrentStep] = useState(0)

    const handleClose = (): void => {
        setModalVisibility(false)
    }

    const handleNext = (): void => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1)
        } else {
            handleClose()
        }
    }

    const handleModalShow = (): void => {
        setCurrentStep(0)
    }

    const currentStepData = steps[currentStep]
    const isLastStep = currentStep === steps.length - 1

    return (
        <Modal
            className="bg-white"
            data-testid="stepped-modal"
            isModalVisible={isModalVisible}
            setModalVisibility={setModalVisibility}
            onShow={handleModalShow}
            onHide={markModalAsSeen}
            size={size}
            footer={
                <div className="flex items-center justify-between w-full">
                    <div className="text-sm text-text-secondary">
                        <DictionaryText
                            textKey={STEPPED_MODAL_MESSAGE_KEYS.STEP_INDICATOR}
                            replacer={{ step: currentStep + 1, totalSteps: steps.length }}
                        />
                    </div>
                    <div className="flex gap-4">
                        {!!currentStep && (
                            <Button
                                variant={ButtonVariants.SECONDARY}
                                onClick={() => setCurrentStep((step) => Math.max(step - 1, 0))}
                            >
                                <DictionaryText textKey={STEPPED_MODAL_MESSAGE_KEYS.BUTTON_PREVIOUS} />
                            </Button>
                        )}
                        <Button variant={ButtonVariants.PRIMARY} onClick={handleNext}>
                            <DictionaryText
                                textKey={
                                    isLastStep
                                        ? STEPPED_MODAL_MESSAGE_KEYS.BUTTON_DONE
                                        : STEPPED_MODAL_MESSAGE_KEYS.BUTTON_NEXT
                                }
                            />
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-4">
                {currentStepData.image && (
                    <div className="order-last [@media(min-height:44rem)]:order-first">{currentStepData.image}</div>
                )}
                <Text type={TextTypes.HEADING_2}>{currentStepData.title}</Text>
                {currentStepData.description}
            </div>
        </Modal>
    )
}
